import type { AxiosHostField, AxiosHostConfig, URLType, AxiosHostURL, PortType, AxiosHostPort, AxiosHostEnv } from './declare'
import axios from 'axios'
import $consts from 'src/plugins/consts'
import $cookie from 'src/plugins/cookies'
import { axiosConfig } from './config'

const axiosIns = axios.create(axiosConfig)

const axiosSpace = {
  CancelToken: axios.CancelToken,
  Request: axiosIns.interceptors.request,
  Response: axiosIns.interceptors.response,
}

// 获取环境配置
const axiosHost = (field: AxiosHostField = 'api', config?: AxiosHostConfig) => {
  // 如果服务端指定基础地址，则以服务端为主
  const getCookieURL = (key?: string) => {
    return key ? $cookie.getItem(key) : null
  }

  // url 本身是否已经带端口了，如果已经带端口，则不附加配置的端口
  const urlMatchPort = (url: string) => {
    const index = url.lastIndexOf(':')
    if (index !== -1) {
      const port = url.substring(index + 1)
      return port && !isNaN(Number(port))
    }
    return false
  }

  // 如果默认端口是 80 或者 443 则 localPort 是一个空字符串
  const { protocol, host, hostname, port: localPort } = window.location
  const isHttps = protocol.startsWith('https')
  const autoPort = isHttps ? 443 : 80
  const getUrl = (url: URLType, allowUndefined: boolean = true) => {
    if (allowUndefined && url === undefined) return undefined
    return url && url !== 'AUTO' ? url : `${config?.envType ? `${config.envType}:` : ''}//${hostname}`
  }
  const getPort = (port: PortType, allowUndefined: boolean = true) => {
    if (allowUndefined && port === undefined) return undefined
    return port ? (port === 'AUTO' ? (localPort || autoPort) : port) : (config?.missMatchPort || autoPort)
  }

  // 如果服务端指定基础地址，则以服务端为主
  const cookieUrl = getCookieURL(config?.cookieField)
  if (cookieUrl) return cookieUrl

  // CONFIG 为子模块配置文件，ENV 为 public->config.js 里的配置文件
  // 存在全局配置时，以全局配置为主
  const isGlobalEnvValid = window.global.env?.length
  const urlConfig: AxiosHostURL = isGlobalEnvValid ? $consts['ENV/URL_CONFIG'] : $consts['CONFIG/URL_CONFIG']
  const portConfig: AxiosHostPort = isGlobalEnvValid ? $consts['ENV/PORT_CONFIG'] : $consts['CONFIG/PORT_CONFIG']
  const envConfig: AxiosHostEnv = isGlobalEnvValid ? $consts['ENV/ENV_CONFIG'] : $consts['CONFIG/ENV_CONFIG']

  // 默认 host
  const envUrl = getUrl(urlConfig?.[field], false)
  const envPort = getPort(portConfig?.[field], false)

  // 配置 host
  const configEnv = envConfig?.find(config => {
    return config.hostname.find(name => {
      const isHost = urlMatchPort(name)
      // 在 window.location 中 host 带端口，hostname 不带端口
      return name.indexOf(isHost ? host : hostname) !== -1
    })
  })
  const configUrl = getUrl(configEnv?.url?.[field])
  const configPort = getPort(configEnv?.port?.[field])

  // 配置 host 如果为 undefined，则取默认 host
  const targetUrl = configUrl || envUrl
  const targetPort = configPort || envPort

  return config?.hostname || urlMatchPort(targetUrl!) ? targetUrl! : `${targetUrl}:${targetPort}`
}

// 获取 axios baseURL
const axiosBase = (prefixPath?: string) => {
  return `${axiosHost('api', { cookieField: $consts['STORAGE/COOKIE_BASEURL'] })}${prefixPath ?? ''}`
}

export { axiosSpace, axiosHost, axiosBase }
export default axiosIns